import { useMemo } from "react";
import {
  ChartContainer,
  type ChartConfig,
} from "@/components/ui/chart";
import { PieChart, Pie, Cell, Tooltip } from "recharts";
import type { WeeklyProgressVM } from "./types";

interface Props {
  vm: WeeklyProgressVM;
}

const OTHER_COLOR = "hsl(var(--muted-foreground))";

export function PieTab({ vm }: Props) {
  const { categoryTotals, categoryColors } = vm;

  const slices = useMemo(() => {
    const sorted = [...categoryTotals].sort((a, b) => b.total - a.total);
    if (sorted.length <= 6) return sorted;
    const top5 = sorted.slice(0, 5);
    const otherTotal = sorted.slice(5).reduce((s, c) => s + c.total, 0);
    return [...top5, { category: "Andre", total: otherTotal }];
  }, [categoryTotals]);

  const totalDone = slices.reduce((s, c) => s + c.total, 0);

  const chartConfig = useMemo<ChartConfig>(() => {
    const cfg: ChartConfig = {};
    for (const s of slices) {
      cfg[s.category] = {
        label: s.category,
        color:
          s.category === "Andre"
            ? OTHER_COLOR
            : categoryColors[s.category]?.stroke ?? OTHER_COLOR,
      };
    }
    return cfg;
  }, [slices, categoryColors]);

  if (totalDone === 0) {
    return (
      <p className="text-sm text-muted-foreground text-center py-8">
        Ingen oppgaver fullført i perioden
      </p>
    );
  }

  return (
    <div className="flex flex-col sm:flex-row items-center gap-4">
      <ChartContainer config={chartConfig} className="h-44 w-44 mx-auto sm:mx-0">
        <PieChart>
          <Pie
            data={slices}
            dataKey="total"
            nameKey="category"
            innerRadius="55%"
            outerRadius="90%"
            paddingAngle={2}
            strokeWidth={0}
          >
            {slices.map((s) => (
              <Cell
                key={s.category}
                fill={
                  s.category === "Andre"
                    ? OTHER_COLOR
                    : categoryColors[s.category]?.stroke ?? OTHER_COLOR
                }
              />
            ))}
          </Pie>
          <Tooltip
            content={({ active, payload }) => {
              if (!active || !payload?.length) return null;
              const item = payload[0];
              const pct = totalDone > 0 ? Math.round(((item.value as number) / totalDone) * 100) : 0;
              return (
                <div className="rounded-lg border bg-background px-3 py-2 text-xs shadow-xl">
                  <span className="font-medium">{item.name}</span>
                  <span className="ml-2 text-muted-foreground">
                    {item.value} ({pct}%)
                  </span>
                </div>
              );
            }}
          />
        </PieChart>
      </ChartContainer>

      {/* Legend */}
      <div className="flex flex-col gap-1 text-xs">
        {slices.map((s) => {
          const pct =
            totalDone > 0
              ? Math.round((s.total / totalDone) * 100)
              : 0;
          return (
            <div key={s.category} className="flex items-center gap-1.5">
              <span
                className="inline-block h-2 w-2 rounded-full shrink-0"
                style={{
                  backgroundColor:
                    s.category === "Andre"
                      ? OTHER_COLOR
                      : categoryColors[s.category]?.stroke ?? OTHER_COLOR,
                }}
              />
              <span className="text-muted-foreground">{s.category}</span>
              <span className="ml-auto font-medium tabular-nums">
                {s.total} ({pct}%)
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}
